import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { device } from "../../../atoms/Utlis/media";
import {
  MessageCircle,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  XCircle,
  Wallet,
  Phone,
  RefreshCw,
  Megaphone,
  FileText,
  Zap,
  Sparkles,
  BookOpen,
  Smartphone,
} from "lucide-react";
import EmptyState from "../../../atoms/Dashboard/WhatsApp/EmptyState";
import { useWhatsApp } from "../../../../hooks/useWhatsApp";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import {
  getWhatsAppTemplates,
  getWhatsAppCampaigns,
} from "../../../../Redux/ApiCalls/Dashboard/WhatsAppAPI";
import { SkeletonDashboard } from "../../../atoms/Dashboard/WhatsApp/SkeletonLoader";
import { showErrorToast } from "../../../atoms/Utlis/Toast";
import { WhatsAppUserGuideModal } from "./WhatsAppUserGuideModal";
import {
  PageContainer,
  Title,
  Subtitle,
  RefreshButton,
  ErrorText,
} from "./whatsappSharedStyles";

const WhatsAppDashboardPage: React.FC = () => {
  const [showGuideModal, setShowGuideModal] = useState(false);
  const dispatch = useAppDispatch();
  const { templates, campaigns } = useAppSelector((state) => state.whatsapp);
  const { whatsAppAccount, walletBalance, loading, error, isError, refresh } =
    useWhatsApp();

  const walletData = walletBalance?.data;
  const isConnected = whatsAppAccount?.connected ?? false;
  const defaultPhone =
    whatsAppAccount?.phone_numbers?.find((p) => p.is_default) ||
    whatsAppAccount?.phone_numbers?.[0];
  const phoneCount = whatsAppAccount?.phone_numbers?.length ?? 0;
  const accessBlockedMessage =
    error?.account?.trim() ||
    "Connect your WhatsApp Business account first to open messages, wallet balance, phone numbers, plan upgrades, campaigns, billing, and automation.";

  useEffect(() => {
    if (isConnected) {
      dispatch(getWhatsAppTemplates(undefined));
      dispatch(getWhatsAppCampaigns(undefined));
    }
  }, [dispatch, isConnected]);

  const handleRefresh = () => {
    refresh();
    if (isConnected) {
      dispatch(getWhatsAppTemplates(undefined));
      dispatch(getWhatsAppCampaigns(undefined));
    }
  };

  const handleRestrictedClick = (
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    if (!isConnected || Boolean(error?.account)) {
      event.preventDefault();
      showErrorToast(accessBlockedMessage);
    }
  };

  const formatCurrency = (amount?: string, currency?: string) => {
    if (amount == null) return "—";
    const symbol = currency === "INR" || !currency ? "₹" : currency;
    return `${symbol}${parseFloat(amount).toLocaleString()}`;
  };

  const balance = Number(walletData?.balance || 0);
  const hasRecharged = balance > 0;
  const hasTemplates = Array.isArray(templates) && templates.length > 0;
  const hasCampaigns = Array.isArray(campaigns) && campaigns.length > 0;

  const renderNextActionButton = () => {
    if (!isConnected) {
      return (
        <NextStepButton to="/dashboard/whatsapp-setup">
          <Smartphone size={16} />
          <span>Setup WhatsApp</span>
          <ArrowRight size={15} />
        </NextStepButton>
      );
    }

    // 1. Onboarded and haven't recharged (amount is 0) -> Recharge wallet
    if (!hasRecharged || balance === 0) {
      return (
        <NextStepButton to="/dashboard/whatsapp/billing">
          <Wallet size={16} />
          <span>Recharge Wallet</span>
          <ArrowRight size={15} />
        </NextStepButton>
      );
    }

    // 2. Recharge is done and user doesn't have created templates -> Create template
    if (!hasTemplates) {
      return (
        <NextStepButton to="/dashboard/whatsapp/templates?create=true">
          <FileText size={16} />
          <span>Create Template</span>
          <ArrowRight size={15} />
        </NextStepButton>
      );
    }

    // 3. Template is created but campaign is not created and shared -> Create campaign
    if (!hasCampaigns) {
      return (
        <NextStepButton to="/dashboard/whatsapp/campaigns?create=true">
          <Megaphone size={16} />
          <span>Create Campaign</span>
          <ArrowRight size={15} />
        </NextStepButton>
      );
    }

    // 4. Campaign is also created -> Start conversation
    return (
      <NextStepButton to="/dashboard/whatsapp/conversations">
        <MessageCircle size={16} />
        <span>Start Conversation</span>
        <ArrowRight size={15} />
      </NextStepButton>
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <SkeletonDashboard />
      </PageContainer>
    );
  }

  const featureCards = [
    {
      to: "/dashboard/whatsapp/templates",
      icon: <FileText size={22} strokeWidth={2} />,
      label: "Templates",
      desc: "Manage approved message templates",
      accent: "templates" as const,
    },
    {
      to: "/dashboard/whatsapp/campaigns",
      icon: <Megaphone size={22} strokeWidth={2} />,
      label: "Campaigns",
      desc: "Broadcast messages at scale",
      accent: "campaigns" as const,
    },
    {
      to: "/dashboard/whatsapp/conversations",
      icon: <MessageCircle size={22} strokeWidth={2} />,
      label: "Conversations",
      desc: "Reply to customers in real time",
      accent: "conversations" as const,
    },
    /*
    {
      to: "/dashboard/whatsapp/automation",
      icon: <Zap size={22} strokeWidth={2} />,
      label: "Automation",
      desc: "Set up smart reply flows",
      accent: "automation" as const,
    },
    */
    {
      to: "/dashboard/whatsapp/billing",
      icon: <Wallet size={22} strokeWidth={2} />,
      label: "Billing & usage",
      desc: "Wallet, plans, and spend",
      accent: "billing" as const,
    },
  ];

  return (
    <PageContainer>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <div className="flex flex-col">
              <HeroBadge>
                <Sparkles size={14} />
                WhatsApp Business
              </HeroBadge>
              <Title className="linear-text">WhatsApp CRM</Title>
              <Subtitle>
                Manage conversations, campaigns, and usage from one place
              </Subtitle>
            </div>
          </HeroText>
          <HeroActions>
            <UserGuideButton
              type="button"
              onClick={() => setShowGuideModal(true)}
              title="Open WhatsApp CRM User Guide"
            >
              <BookOpen size={16} />
              <span>User Guide</span>
            </UserGuideButton>
            <RefreshButton onClick={handleRefresh} title="Refresh data">
              <RefreshCw size={18} />
            </RefreshButton>
          </HeroActions>
        </HeroContent>
      </Hero>

      {isError && error?.account && <ErrorText>{error.account}</ErrorText>}
      {isError && error?.wallet && <ErrorText>{error.wallet}</ErrorText>}

      <ConnectionCard $connected={isConnected}>
        <ConnectionPulse $connected={isConnected} />
        <ConnectionIcon $connected={isConnected}>
          {isConnected ? (
            <CheckCircle2 size={24} strokeWidth={2.5} />
          ) : (
            <XCircle size={24} strokeWidth={2.5} />
          )}
        </ConnectionIcon>
        <ConnectionContent>
          <ConnectionTitle>
            {isConnected ? "WhatsApp Connected" : "WhatsApp Not Connected"}
          </ConnectionTitle>
          <ConnectionMeta>
            {isConnected ? (
              defaultPhone ? (
                <>
                  <Phone size={13} />
                  {defaultPhone.display_phone_number ||
                    defaultPhone.phone_number ||
                    defaultPhone.integrated_number}
                  {phoneCount > 1 && <MorePhones>+{phoneCount - 1} more</MorePhones>}
                </>
              ) : (
                "Your WhatsApp account is connected."
              )
            ) : (
              "Connect your WhatsApp Business account to get started"
            )}
          </ConnectionMeta>
        </ConnectionContent>
        {!isConnected && (
          <SetupButton to="/dashboard/whatsapp-setup">
            Setup WhatsApp
            <ArrowRight size={16} />
          </SetupButton>
        )}
        {isConnected && (
          <StatusPill $connected>
            <StatusDot />
            Live
          </StatusPill>
        )}
      </ConnectionCard>

      <StatsRow>
        <WalletHero
          as={Link}
          to="/dashboard/whatsapp/billing"
          onClick={handleRestrictedClick}
        >
          <WalletIconWrap>
            <Wallet size={16} strokeWidth={2} />
          </WalletIconWrap>
          <WalletHeroBody>
            <WalletHeroValue>
              {walletData
                ? formatCurrency(walletData.balance, walletData.currency)
                : "—"}
            </WalletHeroValue>
            <WalletHeroLabel>Wallet balance</WalletHeroLabel>
            <WalletHeroFooter>
              <span>
                {walletData
                  ? `${walletData.template_conversations_billed?.toLocaleString() ?? 0} billed chats`
                  : "View billing & top-up"}
              </span>
              <ArrowRight size={14} />
            </WalletHeroFooter>
          </WalletHeroBody>
        </WalletHero>

        <StatCard
          as={Link}
          to="/dashboard/whatsapp/conversations"
          onClick={handleRestrictedClick}
        >
          <StatIconWrap $tone="conversations">
            <MessageCircle size={16} strokeWidth={2} />
          </StatIconWrap>
          <StatContent>
            <StatValue>
              {walletData
                ? walletData.template_conversations_billed?.toLocaleString() ?? "0"
                : "0"}
            </StatValue>
            <StatLabel>Conversations billed</StatLabel>
            <StatHint>Open inbox →</StatHint>
          </StatContent>
        </StatCard>

        <StatCard
          as={Link}
          to="/dashboard/whatsapp/billing"
          onClick={handleRestrictedClick}
        >
          <StatIconWrap $tone="spend">
            <BarChart3 size={16} strokeWidth={2} />
          </StatIconWrap>
          <StatContent>
            <StatValue>
              {walletData
                ? formatCurrency(walletData.total_debited, walletData.currency)
                : "—"}
            </StatValue>
            <StatLabel>Total spent</StatLabel>
            <StatHint>Usage & upgrade →</StatHint>
          </StatContent>
        </StatCard>
      </StatsRow>

      <SectionHeader>
        <SectionHeaderLeft>
          <SectionTitle>Quick actions</SectionTitle>
          <SectionHint>Jump into the tools you use most</SectionHint>
        </SectionHeaderLeft>
        {renderNextActionButton()}
      </SectionHeader>

      <FeaturesGrid>
        {featureCards.map((card) => (
          <FeatureCard
            key={card.to}
            to={card.to}
            onClick={handleRestrictedClick}
            $accent={card.accent}
          >
            <FeatureIcon $accent={card.accent}>{card.icon}</FeatureIcon>
            <FeatureBody>
              <FeatureLabel>{card.label}</FeatureLabel>
              <FeatureDesc>{card.desc}</FeatureDesc>
            </FeatureBody>
            <FeatureArrow>
              <ArrowRight size={16} />
            </FeatureArrow>
          </FeatureCard>
        ))}
      </FeaturesGrid>

      {!whatsAppAccount && !isError && (
        <EmptyState
          icon={<BarChart3 size={48} />}
          title="No data yet"
          description="Connect your WhatsApp account to see messages sent, usage, and campaign performance here."
        />
      )}

      <WhatsAppUserGuideModal
        isOpen={showGuideModal}
        onClose={() => setShowGuideModal(false)}
      />
    </PageContainer>
  );
};

export default WhatsAppDashboardPage;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.45; transform: scale(1); }
  50% { opacity: 0.85; transform: scale(1.15); }
`;

type Accent =
  | "conversations"
  | "campaigns"
  | "templates"
  | "automation"
  | "billing"
  | "spend";

const accentMap: Record<
  Accent,
  { soft: string; solid: string; border: string }
> = {
  conversations: {
    soft: "rgba(37, 211, 102, 0.12)",
    solid: "#25D366",
    border: "rgba(37, 211, 102, 0.25)",
  },
  campaigns: {
    soft: "rgba(206, 86, 176, 0.12)",
    solid: "#CE56B0",
    border: "rgba(206, 86, 176, 0.25)",
  },
  templates: {
    soft: "rgba(59, 130, 246, 0.12)",
    solid: "#3B82F6",
    border: "rgba(59, 130, 246, 0.25)",
  },
  automation: {
    soft: "rgba(245, 158, 11, 0.14)",
    solid: "#F59E0B",
    border: "rgba(245, 158, 11, 0.28)",
  },
  billing: {
    soft: "rgba(201, 77, 170, 0.12)",
    solid: "#C94DAA",
    border: "rgba(201, 77, 170, 0.25)",
  },
  spend: {
    soft: "rgba(139, 92, 246, 0.12)",
    solid: "#8B5CF6",
    border: "rgba(139, 92, 246, 0.25)",
  },
};

const Hero = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  padding: 28px 28px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.18) 0%,
    rgba(206, 86, 176, 0.14) 55%,
    rgba(37, 211, 102, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  animation: ${fadeUp} 0.35s ease-out;
  @media ${device.tablet} {
    padding: 22px 18px 18px;
  }
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -40%;
  right: -8%;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 201, 81, 0.35) 0%,
    transparent 70%
  );
`;

const HeroContent = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const HeroText = styled.div`
  min-width: 0;
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  margin-bottom: 4px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(206, 86, 176, 0.2);
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #ce56b0;
  backdrop-filter: blur(6px);
`;

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const UserGuideButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  color: #171717;
  font-family: "Urbanist", sans-serif;
  font-size: 13.5px;
  font-weight: 700;
  border: 1.5px solid rgba(206, 86, 176, 0.35);
  box-shadow: 0 4px 14px rgba(206, 86, 176, 0.1);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  white-space: nowrap;

  svg {
    color: #ce56b0;
    transition: color 0.2s ease;
  }

  &:hover {
    background: linear-gradient(
      144.01deg,
      rgba(255, 201, 81, 0.95) 0%,
      rgba(206, 86, 176, 0.95) 100%
    );
    color: #ffffff;
    border-color: transparent;
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(201, 77, 170, 0.28);

    svg {
      color: #ffffff;
    }
  }

  &:active {
    transform: translateY(0);
  }

  @media ${device.mobileL} {
    padding: 8px 12px;
    font-size: 12px;
  }
`;

const ConnectionCard = styled.div<{ $connected: boolean }>`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px 22px;
  border-radius: 18px;
  background: ${(p) =>
    p.$connected
      ? "linear-gradient(135deg, rgba(37, 211, 102, 0.1) 0%, rgba(37, 211, 102, 0.02) 100%)"
      : "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.02) 100%)"};
  border: 1px solid
    ${(p) =>
    p.$connected ? "rgba(37, 211, 102, 0.22)" : "rgba(239, 68, 68, 0.22)"};
  box-shadow: 0 8px 28px
    ${(p) =>
    p.$connected
      ? "rgba(37, 211, 102, 0.08)"
      : "rgba(239, 68, 68, 0.06)"};
  animation: ${fadeUp} 0.4s ease-out 0.05s both;
`;

const ConnectionPulse = styled.div<{ $connected: boolean }>`
  display: ${(p) => (p.$connected ? "block" : "none")};
  position: absolute;
  left: 34px;
  top: 34px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(37, 211, 102, 0.35);
  animation: ${pulse} 2.2s ease-in-out infinite;
`;

const ConnectionIcon = styled.div<{ $connected: boolean }>`
  position: relative;
  z-index: 1;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => (p.$connected ? "#16a34a" : "#ef4444")};
  background: ${(p) =>
    p.$connected ? "rgba(37, 211, 102, 0.14)" : "rgba(239, 68, 68, 0.12)"};
`;

const ConnectionContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ConnectionTitle = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: #171717;
  margin-bottom: 4px;
`;

const ConnectionMeta = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.65);
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const MorePhones = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(37, 211, 102, 0.12);
  color: #15803d;
  font-size: 11px;
  font-weight: 600;
`;

const SetupButton = styled(Link)`
  background: linear-gradient(
    144.01deg,
    rgba(255, 201, 81, 0.95) 0%,
    rgba(206, 86, 176, 0.95) 100%
  );
  color: white;
  padding: 11px 20px;
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0px 6px 18px rgba(201, 77, 170, 0.28);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0px 10px 24px rgba(201, 77, 170, 0.35);
  }
`;

const StatusPill = styled.div<{ $connected?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(37, 211, 102, 0.25);
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #15803d;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.18);
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  animation: ${fadeUp} 0.45s ease-out 0.1s both;
  @media ${device.tablet} {
    grid-template-columns: 1fr;
  }
`;

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  text-decoration: none;
  color: #fff;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.85) 0%, rgba(206, 86, 176, 0.85) 100%);
  box-shadow: 0 8px 22px rgba(201, 77, 170, 0.22);
  text-align: left;
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  &::after {
    content: "";
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 26px rgba(201, 77, 170, 0.28);
  }
`;

const WalletIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(4px);
`;

const WalletHeroBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
  flex: 1;
`;

const WalletHeroValue = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
`;

const WalletHeroLabel = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
`;

const WalletHeroFooter = styled.div`
  position: relative;
  z-index: 1;
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-family: "Montserrat", sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
`;

const StatCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 14px 16px;
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
  border: 1px solid rgba(23, 23, 23, 0.06);
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(23, 23, 23, 0.08);
    border-color: rgba(201, 77, 170, 0.18);
  }
`;

const StatIconWrap = styled.div<{ $tone: Accent }>`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => accentMap[p.$tone].solid};
  background: ${(p) => accentMap[p.$tone].soft};
  flex-shrink: 0;
`;

const StatContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const StatValue = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`;

const StatLabel = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`;

const StatHint = styled.div`
  margin-top: 4px;
  font-family: "Montserrat", sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #ce56b0;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 4px;
`;

const SectionHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const NextStepButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 20px;
  border-radius: 12px;
  background: linear-gradient(
    144.01deg,
    rgba(255, 201, 81, 0.95) 0%,
    rgba(206, 86, 176, 0.95) 100%
  );
  color: #ffffff;
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0px 6px 18px rgba(201, 77, 170, 0.28);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0px 10px 24px rgba(201, 77, 170, 0.38);
    color: #ffffff;
  }

  &:active {
    transform: translateY(0);
  }

  @media ${device.tablet} {
    padding: 8px 14px;
    font-size: 13px;
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-family: "Urbanist", sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #171717;
`;

const SectionHint = styled.p`
  margin: 0;
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
  animation: ${fadeUp} 0.5s ease-out 0.15s both;
  @media ${device.tablet} {
    grid-template-columns: 1fr;
  }
`;

const FeatureIcon = styled.div<{ $accent: Accent }>`
  width: 46px;
  height: 46px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => accentMap[p.$accent].solid};
  background: ${(p) => accentMap[p.$accent].soft};
`;

const FeatureBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const FeatureLabel = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #171717;
  margin-bottom: 3px;
`;

const FeatureDesc = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.58);
  line-height: 1.35;
`;

const FeatureArrow = styled.div`
  color: #c4c4c4;
  opacity: 0.7;
  transition: all 0.2s ease;
  flex-shrink: 0;
`;

const FeatureCard = styled(Link) <{ $accent: Accent }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: 16px;
  text-decoration: none;
  color: inherit;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
    border-color: ${(p) => accentMap[p.$accent].border};
  }
  &:hover ${FeatureArrow} {
    opacity: 1;
    transform: translateX(2px);
    color: ${(p) => accentMap[p.$accent].solid};
  }
`;
